import { Subscription, type Observable } from 'rxjs';
import { isSketchRelationContainer } from '../../../sketch/solver/constraint/SketchRelationContainer';
import { isSketchObject } from '../../../mesh/guards/isSketchObject';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import type { SketchEntityReferenceRecord } from '../model/SketchEntityReferenceRecord';
import type {
	SketchEntityReferenceAutomaticLifecycleEvent,
	SketchEntityReferenceAutomaticLifecycleResult,
	SketchEntityReferenceLifecycleCoordinatorDependencies,
	SketchEntityReferenceRemoveRelationInput,
	SketchEntityReferenceRemoveRelationResult,
	SketchEntityReferenceSketchEntitiesRemovedEvent,
	SketchEntityReferenceSourceSketchRemovedEvent,
	SketchEntityReferenceTopologyOwnerRemovedEvent,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceLifecycleCoordinator';

export type {
	SketchEntityReferenceAutomaticLifecycleEvent,
	SketchEntityReferenceAutomaticLifecycleResult,
	SketchEntityReferenceLifecycleCoordinatorDependencies,
	SketchEntityReferenceRemoveRelationInput,
	SketchEntityReferenceRemoveRelationResult,
	SketchEntityReferenceSketchEntitiesRemovedEvent,
	SketchEntityReferenceSourceSketchRemovedEvent,
	SketchEntityReferenceTopologyOwnerRemovedEvent,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceLifecycleCoordinator';

/**
 * 当前文档的转换实体引用生命周期协调器。
 *
 * 本类只负责事件时序和业务路由：目标离场进入真实删除，草图来源离场进入 dangling，
 * topology owner 离场等待特征历史上下文判断。异步队列永远只保存纯数据事件。
 */
export class SketchEntityReferenceLifecycleCoordinator {
	/** 当前激活周期的事件订阅。 */
	private subscription: Subscription | null = null;
	/** 等待串行处理的纯数据事件。 */
	private readonly pendingEvents: SketchEntityReferenceAutomaticLifecycleEvent[] = [];
	/** 防止并行消费同一文档的生命周期事件。 */
	private isDrainingQueue = false;
	/** 防止释放后重新订阅或执行命令。 */
	private disposed = false;

	/** @param dependencies 文档事件源、仓库和两项生命周期服务。 */
	public constructor(
		private readonly dependencies: SketchEntityReferenceLifecycleCoordinatorDependencies,
	) {}

	/** 激活文档级事件监听；重复调用保持幂等。 */
	public activate(): void {
		this.assertUsable();
		if (this.subscription) {
			return;
		}

		const subscription = new Subscription();
		try {
			this.bindEventSource(subscription, this.dependencies.observeSketchEntitiesRemoved());
			this.bindEventSource(subscription, this.dependencies.observeSourceSketchRemoved());
			this.bindEventSource(subscription, this.dependencies.observeTopologyOwnerRemoved());
			this.subscription = subscription;
		} catch (error) {
			subscription.unsubscribe();
			throw error;
		}
	}

	/**
	 * 删除用户选中的草图关系。
	 *
	 * 普通关系直接交给约束容器；引用关系必须先由删除 Service 完成仓库和运行时一致性处理。
	 *
	 * @param input 当前目标与约束 ID。
	 * @returns 手动关系删除结果。
	 */
	public async removeRelation(
		input: SketchEntityReferenceRemoveRelationInput,
	): Promise<SketchEntityReferenceRemoveRelationResult> {
		this.assertUsable();
		const constraintContainer = Reflect.get(input.targetEntity, 'constraints');
		if (!isSketchRelationContainer(constraintContainer)) {
			return this.createManualFailure(input, '目标实体不支持关系删除');
		}

		const constraint = constraintContainer.getRelation(input.constraintId);
		if (!constraint) {
			const readDerivedView = Reflect.get(constraintContainer, 'getDerivedRelationSnapshot');
			const derivedView = typeof readDerivedView === 'function'
				? readDerivedView.call(constraintContainer, input.constraintId)
				: undefined;

			if (derivedView?.isReadOnly && derivedView.sourceDimensionId) {
				return this.createManualFailure(
					input,
					`“${derivedView.baseDisplayName}”是尺寸 ${derivedView.sourceDimensionId} 的只读关系视图，请删除或编辑对应尺寸`,
					'warning',
				);
			}
			return this.createManualFailure(input, '未找到待删除的草图关系', 'warning');
		}
		if (constraint.isReadOnly) {
			return this.createManualFailure(
				input,
				`“${constraint.baseDisplayName}”由其阵列定义托管，请编辑或删除对应阵列`,
				'warning',
			);
		}

		const referenceId = this.resolveReferenceId(constraint);
		if (!referenceId) {
			const removed = this.removeConstraintFromAllTargets(
				constraint,
				constraintContainer,
				input.constraintId,
			);
			return {
				removedReferenceIds: [],
				removedReferences: [],
				updatedReferences: [],
				removedConstraintIds: removed ? [input.constraintId] : [],
				referenceRelationRemoved: false,
				diagnostics: [],
			};
		}

		const result = await this.dependencies.removeService.detachReferencesKeepingTargets([referenceId]);
		const item = result.items.find((candidate) => candidate.referenceId === referenceId);
		if (item?.status === 'removed' || item?.status === 'detached') {
			return {
				removedReferenceIds: result.removedReferenceIds,
				removedReferences: result.removedReferences,
				updatedReferences: result.updatedReferences ?? [],
				removedConstraintIds: [input.constraintId],
				referenceRelationRemoved: true,
				diagnostics: result.diagnostics,
			};
		}

		if (item?.status === 'not-found') {
			const removed = this.removeConstraintFromAllTargets(
				constraint,
				constraintContainer,
				input.constraintId,
			);
			return {
				removedReferenceIds: [],
				removedReferences: [],
				updatedReferences: [],
				removedConstraintIds: removed ? [input.constraintId] : [],
				referenceRelationRemoved: removed,
				diagnostics: result.diagnostics,
			};
		}

		return {
			removedReferenceIds: result.removedReferenceIds,
			removedReferences: result.removedReferences,
			updatedReferences: result.updatedReferences ?? [],
			removedConstraintIds: [],
			referenceRelationRemoved: false,
			diagnostics: result.diagnostics,
		};
	}

	/**
	 * 从关系全部直接目标的容器中删除同一关系实例。
	 *
	 * 多目标关系共享一个 constraint；只删当前 PropertyManager 对象会让其余目标继续持有
	 * 半条关系。历史数据或测试替身缺少目标快照协议时，退回当前容器完成最小清理。
	 *
	 * @param constraint 待删除关系实例。
	 * @param fallbackContainer 当前目标约束容器。
	 * @param constraintId 关系运行时 ID。
	 * @returns 是否至少从一个容器删除成功。
	 */
	private removeConstraintFromAllTargets(
		constraint: object,
		fallbackContainer: {removeRelation(constraintId: number): boolean},
		constraintId: number,
	): boolean {
		const readTargets = Reflect.get(constraint, 'getTargetsSnapshot');
		if (typeof readTargets !== 'function') {
			return fallbackContainer.removeRelation(constraintId);
		}

		const targets = readTargets.call(constraint);
		if (!Array.isArray(targets) || targets.length === 0) {
			return fallbackContainer.removeRelation(constraintId);
		}

		let removed = false;
		targets.forEach((target) => {
			const container = target && typeof target === 'object'
				? Reflect.get(target, 'constraints')
				: null;
			if (isSketchRelationContainer(container)) {
				removed = container.removeRelation(constraintId) || removed;
			}
		});
		return removed;
	}

	/** 释放订阅和尚未处理的事件；重复调用保持幂等。 */
	public dispose(): void {
		if (this.disposed) {
			return;
		}
		this.disposed = true;
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.pendingEvents.length = 0;
		this.isDrainingQueue = false;
	}

	/**
	 * 将一个可选事件源绑定到当前订阅容器。
	 *
	 * @param subscription 当前激活周期订阅。
	 * @param event$ 纯数据事件源。
	 */
	private bindEventSource<TEvent extends SketchEntityReferenceAutomaticLifecycleEvent>(
		subscription: Subscription,
		event$: Observable<TEvent> | null,
	): void {
		if (!event$) {
			return;
		}
		subscription.add(event$.subscribe((event) => this.enqueue(event)));
	}

	/** @param event 新到达的纯数据生命周期事件。 */
	private enqueue(event: SketchEntityReferenceAutomaticLifecycleEvent): void {
		if (this.disposed) {
			return;
		}
		this.pendingEvents.push(event);
		void this.drainQueue();
	}

	/** 串行消费队列，并把每个异常转换为可观察的 failed 结果。 */
	private async drainQueue(): Promise<void> {
		if (this.isDrainingQueue) {
			return;
		}
		this.isDrainingQueue = true;
		try {
			while (this.pendingEvents.length > 0 && !this.disposed) {
				const event = this.pendingEvents.shift();
				if (!event) {
					continue;
				}
				try {
					await this.processEvent(event);
				} catch (error) {
					this.dependencies.reportAutomaticResult({
						eventType: event.type,
						status: 'failed',
						affectedReferenceIds: [],
						affectedTargetEntityIds: [],
						affectedTargetSketchIds: [],
						diagnostics: [createExecutionDiagnostic({
							kind: 'cascade-remove-failed',
							level: 'error',
							message: `转换实体引用生命周期事件处理失败(${event.type}): ${error instanceof Error ? error.message : String(error)}`,
						})],
					});
				}
			}
		} finally {
			this.isDrainingQueue = false;
			if (this.pendingEvents.length > 0 && !this.disposed) {
				void this.drainQueue();
			}
		}
	}

	/** @param event 单个生命周期事件。 */
	private async processEvent(event: SketchEntityReferenceAutomaticLifecycleEvent): Promise<void> {
		if (event.type === 'sketch-entity-removed') {
			await this.processSketchEntitiesRemoved(event);
			return;
		}
		if (event.type === 'source-sketch-removed') {
			await this.processSourceSketchRemoved(event);
			return;
		}
		await this.processTopologyOwnerRemoved(event);
	}

	/** @param event 同一草图实体删除批次。 */
	private async processSketchEntitiesRemoved(
		event: SketchEntityReferenceSketchEntitiesRemovedEvent,
	): Promise<void> {
		const references = await this.dependencies.repository.listReferencesBySketchEntities({
			sketchId: event.persistentSketchId,
			entityIds: event.removedEntityIds,
		});
		const removedEntityIds = new Set(event.removedEntityIds);
		const targetReferences: SketchEntityReferenceRecord[] = [];
		const sourceReferences: SketchEntityReferenceRecord[] = [];
		for (const reference of references) {
			const targetMatches = reference.targetSketchId === event.persistentSketchId
				&& removedEntityIds.has(reference.targetEntityId);
			if (targetMatches) {
				targetReferences.push(reference);
				continue;
			}
			const source = reference.sourceDescriptor;
			if (source.kind === 'sketch-entity'
				&& source.sourceSketchId === event.persistentSketchId
				&& removedEntityIds.has(source.entityId)) {
				sourceReferences.push(reference);
			}
		}

		const removeResult = targetReferences.length > 0
			? await this.dependencies.removeService.removeReferencesWithTargets(
				targetReferences.map((reference) => reference.referenceId),
			)
			: null;
		const invalidationResult = sourceReferences.length > 0
			? await this.dependencies.invalidationService.invalidate({
				referenceIds: sourceReferences.map((reference) => reference.referenceId),
			})
			: null;
		const diagnostics = [
			...(removeResult?.diagnostics ?? []),
			...(invalidationResult?.diagnostics ?? []),
		];
		const failed = removeResult?.items.some((item) => item.status === 'failed')
			|| invalidationResult?.items.some((item) => item.status === 'failed')
			|| false;
		this.dependencies.reportAutomaticResult({
			eventType: event.type,
			status: failed ? 'failed' : 'completed',
			affectedReferenceIds: this.unique([
				...(removeResult?.removedReferenceIds ?? []),
				...(invalidationResult?.affectedReferenceIds ?? []),
			]),
			affectedTargetEntityIds: this.unique([
				...(removeResult?.removedTargetEntityIds ?? []),
				...(invalidationResult?.affectedTargetEntityIds ?? []),
			]),
			affectedTargetSketchIds: this.unique([
				...(removeResult?.removedReferences.map((reference) => reference.targetSketchId) ?? []),
				...(invalidationResult?.affectedTargetSketchIds ?? []),
			]),
			diagnostics,
		});
	}

	/** @param event 来源草图删除事件。 */
	private async processSourceSketchRemoved(
		event: SketchEntityReferenceSourceSketchRemovedEvent,
	): Promise<void> {
		const references = await this.dependencies.repository.listReferencesBySourceSketch(
			event.persistentSketchId,
		);
		const result = await this.dependencies.invalidationService.invalidate({
			referenceIds: references.map((reference) => reference.referenceId),
		});
		this.dependencies.reportAutomaticResult({
			eventType: event.type,
			status: result.items.some((item) => item.status === 'failed') ? 'failed' : 'completed',
			affectedReferenceIds: result.affectedReferenceIds,
			affectedTargetEntityIds: result.affectedTargetEntityIds,
			affectedTargetSketchIds: result.affectedTargetSketchIds,
			diagnostics: result.diagnostics,
		});
	}

	/** @param event topology owner 离场事件。 */
	private async processTopologyOwnerRemoved(
		event: SketchEntityReferenceTopologyOwnerRemovedEvent,
	): Promise<void> {
		const references = await this.dependencies.repository.listReferencesByTopologyOwner({
			ownerKind: event.ownerKind,
			ownerId: event.ownerId,
			bodyBindingId: event.bodyBindingId,
			matchAnyBodyBinding: event.matchAnyBodyBinding,
		});
		this.dependencies.reportAutomaticResult({
			eventType: event.type,
			status: 'deferred-history-evaluation',
			affectedReferenceIds: references.map((reference) => reference.referenceId),
			affectedTargetEntityIds: this.unique(references.map((reference) => reference.targetEntityId)),
			affectedTargetSketchIds: this.unique(references.map((reference) => reference.targetSketchId)),
			diagnostics: references.length === 0 ? [] : [createExecutionDiagnostic({
				kind: 'restore-skipped',
				level: 'warning',
				message: `topology owner 已离场，需在特征历史上下文中判断 ${references.length} 条转换实体引用`,
			})],
		});
	}

	/** @param constraint 求解约束。 */
	private resolveReferenceId(constraint: unknown): string | null {
		if (!constraint || typeof constraint !== 'object') {
			return null;
		}
		const referenceId = Reflect.get(constraint, 'referenceId');
		return typeof referenceId === 'string' && referenceId.length > 0 ? referenceId : null;
	}

	/**
	 * 构造手动关系输入错误结果。
	 *
	 * @param input 手动输入。
	 * @param message 错误说明。
	 * @param level 诊断级别。
	 */
	private createManualFailure(
		input: SketchEntityReferenceRemoveRelationInput,
		message: string,
		level: ExecutionDiagnostic['level'] = 'error',
	): SketchEntityReferenceRemoveRelationResult {
		const targetEntityId = isSketchObject(input.targetEntity)
			? input.targetEntity.persistentId
			: undefined;
		return {
			removedReferenceIds: [],
			removedReferences: [],
			updatedReferences: [],
			removedConstraintIds: [],
			referenceRelationRemoved: false,
			diagnostics: [createExecutionDiagnostic({
				kind: 'input-invalid',
				level,
				message: `${message}: ${targetEntityId ?? input.targetEntity.uuid}`,
				targetEntityId,
			})],
		};
	}

	/** @param values 含重复项的字符串集合。 */
	private unique(values: readonly string[]): string[] {
		return Array.from(new Set(values));
	}

	/** 阻止已释放协调器继续建立订阅或执行命令。 */
	private assertUsable(): void {
		if (this.disposed) {
			throw new Error('转换实体引用生命周期协调器已释放，不能继续使用');
		}
	}
}
